import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { signOutAction } from '../../action_creators/user_action_creators';
import {
  LOGIN_ROUTE,
  STRIPE_CHECKOUT_RETURN_URL,
  routeAbsoluteUrl,
} from '../../services/routes';
import {
  createStripeCheckoutSessionOperation,
  updateMembershipNoTierAvailableOperation,
} from '../../operations/user_profile_operations';
import MembershipTierSelectionView from './membership_tier_selection_view';
import { linkWithQueryParams } from '../../services/urls';
import { trackEvents } from '../../operations/tracking_operations';
import SubscribeContent from './subscribe_content';
import {
  appSyncGraphQLApi,
  useGetMembershipTiersBySpaceQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { MembershipTierState, UpdateMembershipResult } from '../../API';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { spaceMembershipWithBackendUpdateMembershipResult } from '../../services/end_user_space_memberships';

const stripeCheckoutAbsoluteReturnUrl = routeAbsoluteUrl(
  STRIPE_CHECKOUT_RETURN_URL,
);

/**
 * Subscribe screen for Stripe memberships (not IAP)
 */
const SubscribeScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery();

  const [searchParams] = useSearchParams();
  const preselectedMembershipTierID = searchParams.get('chosenMembershipTier');

  const onPurchase = useCallback(
    async (membershipTierID, membershipTierTitle, duration) => {
      setIsLoading(true);
      trackEvents([
        {
          name: 'Clicked to purchase subscription',
          args: {
            subscription_name: membershipTierTitle,
          },
        },
      ]);
      const checkoutSession = await createStripeCheckoutSessionOperation(
        membershipTierID,
        currentSpaceMembership?.id,
        stripeCheckoutAbsoluteReturnUrl,
        duration,
      );
      if (checkoutSession && checkoutSession.url) {
        window.location.href = checkoutSession.url;
      } else {
        setIsLoading(false);
      }
    },
    [currentSpaceMembership?.id],
  );

  const { data: currentSpace } = useGetEndUserSpaceQuery();

  const spaceSubscriptionPageBackgroundColor =
    currentSpace?.subscriptionPageStyle?.backgroundColor;

  const onSignOut = () => {
    dispatch(signOutAction());
    navigate(
      linkWithQueryParams(LOGIN_ROUTE, {
        intoSpace: currentSpace?.id,
      }),
    );
  };

  useEffect(() => {
    if (preselectedMembershipTierID) {
      onPurchase(preselectedMembershipTierID);
    }
  }, [onPurchase, preselectedMembershipTierID]);

  const { data: allMembershipTiers, isLoading: isMembershipTiersLoading } =
    useGetMembershipTiersBySpaceQuery(currentSpace?.id || '', {
      skip: !currentSpace?.id,
    });

  const spaceMayHaveMembershipTiers =
    spaceMembershipLoading ||
    isMembershipTiersLoading ||
    (allMembershipTiers || []).some(
      (membershipTier) => membershipTier.state === MembershipTierState.ACTIVE,
    );

  useEffect(() => {
    if (currentSpaceMembership?.id && !spaceMayHaveMembershipTiers) {
      updateMembershipNoTierAvailableOperation(currentSpaceMembership?.id).then(
        (updateMembershipResponse) => {
          console.log(updateMembershipResponse);
          if (updateMembershipResponse.result === UpdateMembershipResult.OK) {
            dispatch(
              appSyncGraphQLApi.util.invalidateTags(['SpaceMembership']),
            );
            const patchedSpaceMembership =
              spaceMembershipWithBackendUpdateMembershipResult(
                currentSpaceMembership,
                updateMembershipResponse,
              );
            dispatch(
              appSyncGraphQLApi.util.updateQueryData(
                'getSpaceMembershipsByMemberID',
                currentSpaceMembership.memberID,
                (draft) => {
                  const index = draft.findIndex(
                    (m) => m.id === currentSpaceMembership.id,
                  );
                  if (index !== -1) {
                    // eslint-disable-next-line no-param-reassign
                    draft[index] = patchedSpaceMembership;
                  }
                },
              ),
            );
          }
        },
      );
    }
  }, [
    spaceMayHaveMembershipTiers,
    dispatch,
    currentSpaceMembership.id,
    currentSpaceMembership,
  ]);

  const styleOverride = spaceSubscriptionPageBackgroundColor
    ? { backgroundColor: spaceSubscriptionPageBackgroundColor }
    : undefined;

  return (
    <div className="subscription-screen" style={styleOverride}>
      {!preselectedMembershipTierID && (
        <>
          <SubscribeContent spaceID={currentSpace?.id} />
          <MembershipTierSelectionView
            onSelect={onPurchase}
            isLoading={isLoading}
          />
          <div className="spacer">&nbsp;</div>
          <div className="footer">
            <div>
              <Button
                onClick={onSignOut}
                style={{ color: 'var(--app-primary-font-color)' }}>
                Log out
              </Button>
            </div>
            <div>
              <Button
                onClick={() => navigate('/user?hideMobileNavigation=true')}
                style={{ color: 'var(--app-primary-font-color)' }}>
                My Profile
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscribeScreen;
